import React, { createRef, ReactElement, useEffect, useState } from "react";
import { getLanguageString, toLiteral } from "../../../../helper/locale-utils";
import { Form, serializeForm, TextArea } from "../../../shared/forms";
import { required } from "../../../shared/forms/utilities/validations";
import { addNotification } from "../../../shared/notifications/notifications-service";
import * as Api from "../../../../api";
import { addLoading, removeLoading } from "../../../shared/loading/loading-service";
import store from "../../../../redux/store";
import { NotificationHeader, Notification, NotificationBody, Toggle } from "@adeccoux/tag-ds";
import Wysiwyg from "../../../shared/forms/wysiwyg/wysiwyg";
import { useDispatch } from "react-redux";
import { refreshAppLiterals } from "../../../../redux/slices/literalsSlice";

const LiteralEditForm: React.FC<any> = (props: any): ReactElement => {
  const tenantStore = store?.getState()?.tenant;
  const tenant = tenantStore?.selectedTenant;
  const dispatch = useDispatch();
  const { setRefresh, refresh, language, literal, disableForm, customTenantLiteral, onCancel, tenantsInUse } = props;
  const [complex, setComplex] = useState(Boolean(customTenantLiteral ? literal?.translation?.tenantCustom?.complex : (literal?.translation?.complex ?? literal?.complex)));

  useEffect(() => {
    setComplex(Boolean(customTenantLiteral ? literal?.translation?.tenantCustom?.complex : (literal?.translation?.complex ?? literal?.complex)));
  }, [literal, customTenantLiteral]);
  const handleSubmit = async (event: any) => {
    const isValid_HTML = event.target.isValid;
    const values_HTML = serializeForm(event.target);
    if (isValid_HTML) {
      addLoading("handleLiteralSubmit");
      try {
        if (customTenantLiteral) {
          literal?.translation?.tenantCustom?.id
            ? await Api.customiseCustomTenantLiteral(literal?.translation?.tenantCustom?.id, { ...values_HTML, complex, language: literal.translation?.language, key: literal?.key })
            : await Api.customiseCountryLiteral({ ...values_HTML, complex, languageLiteralId: literal?.translation?.id, key: literal?.key, language: literal.translation?.language });
          addNotification({
            type: "success",
            content: toLiteral({ id: customTenantLiteral ? "admin.literal.management.customised.customTenantLiteral.success" : "admin.literal.management.customised.countryLiteral.success" }),
            timer: 3,
          });
        } else {
          literal?.translation
            ? await Api.editCountryLiteralTranslation(literal?.translation?.id, { ...values_HTML, complex, language: literal?.translation?.language, key: literal?.key })
            : await Api.translateCountryLiterals({ ...values_HTML, complex, key: literal.key, language });
          addNotification({
            type: "success",
            content: toLiteral({ id: literal?.translation ? "home.literals.management.editTranslation.notify.success" : "home.literals.management.translateLiteral.notify.success" }),
            timer: 3,
          });
        }
        setComplex(false);
        dispatch(refreshAppLiterals());
      } catch {
        addNotification({ type: "error", content: toLiteral({ id: "home.literals.management.editTranslation.notify.error" }), timer: 3 });
      } finally {
        setRefresh((refresh || 0) + 1);
        onCancel();
        removeLoading("handleLiteralSubmit");
      }
    } else {
      addNotification({
        type: "error",
        content: toLiteral({ id: literal?.translation ? "home.literals.management.editTranslation.notify.error.generic" : "home.literals.management.translateLiteral.notify.error.generic" }),
        timer: 3,
      });
    }
  };

  const showNotifications = () => {
    return (
      <Notification
        className="static-notification mb3"
        data-testid="important-notification"
        warning={true}
        icon={<span className="material-icons-outlined">{customTenantLiteral ? "info" : "warning_amber"} </span>}
        show
      >
        <NotificationHeader>
          {customTenantLiteral
            ? `${toLiteral({ id: "admin.literal.management.countryLiteral.customize.message" })} ${getLanguageString(language)} ${toLiteral({ id: "for" })} ${tenant?.name}`
            : tenantsInUse?.length > 0
            ? `${toLiteral({ id: "literal.management.translatedtenants.affected" })} ${tenantsInUse?.length}`
            : toLiteral({ id: "admin.literal.management.countryLiteral.translation.message.2" })}
        </NotificationHeader>
        {!customTenantLiteral && (
          <NotificationBody>
            {tenantsInUse?.length && (
              <ul>
                {tenantsInUse?.map((tenant_id: any, _index: any) => (
                  <li key={_index}>{tenant_id}</li>
                ))}
              </ul>
            )}
          </NotificationBody>
        )}
      </Notification>
    );
  };

  return (
    <>
      {literal && showNotifications()}
      <Form onSubmit={handleSubmit}>
        <div className="border-round p2 mb2">
          <h4>{customTenantLiteral ? toLiteral({ id: "admin.settings.literal.management.action.customize2" }) : toLiteral({ id: "home.literals.management.modal.title.editTranslation2" })}</h4>
          <div className="mb1 pt2 caption">{toLiteral({ id: "admin.literal.management.default.literal.heading2" })}</div>
          <div className="grey-container pl3 pb3 pr3 pt1 mb3">{literal?.value}</div>

          {!customTenantLiteral && (
            <div className="d-flex align-items_center mb2 g2">
              <label className="caption">{toLiteral({ id: "Complex Literal" })}</label>
              <Toggle id="complex" name="complex" checked={complex} small onChange={(isChecked: any) => setComplex(Boolean(isChecked))} disabled={!!disableForm} />
            </div>
          )}
          {!customTenantLiteral ? (
            complex ? (
              <Wysiwyg
                validations={[required]}
                label={toLiteral({ id: "home.literals.management.addLiteral.value2" })}
                name="value"
                disabled={!!disableForm}
                value={literal?.translation ? literal?.translation?.value : ""}
                emptyFormat={false}
                undo={false}
                redo={false}
                headers={false}
                strikeThrough={false}
                justifyCenter={false}
                justifyLeft={false}
                justifyRight={false}
                justifyFull={false}
                indent={false}
                outdent={false}
                table={false}
                link={false}
                unlink={false}
                image={false}
                bold
                italic
                orderedList
                unorderedList
              />
            ) : (
              <TextArea
                validations={[required]}
                label={toLiteral({ id: "home.literals.management.addLiteral.value2" })}
                name="value"
                type="text"
                validateOnChange={true}
                disabled={!!disableForm}
                value={literal?.translation ? literal?.translation?.value : ""}
              />
            )
          ) : (
            <>
              <div className="mb1 pt2 caption">{toLiteral({ id: "admin.literal.management.country.literal.heading" })}</div>
              <div className="grey-container pl3 pb3 pr3 pt1 mb3">{literal?.translation?.value}</div>
              <div className="d-flex align-items_center mb2 g2">
                <label className="caption">{toLiteral({ id: "Complex Literal" })}</label>
                <Toggle id="complex" name="complex" checked={complex} small onChange={(isChecked: any) => setComplex(Boolean(isChecked))} disabled={!!disableForm} />
              </div>
              {complex ? (
                <Wysiwyg
                  validations={[required]}
                  label={toLiteral({ id: "home.literals.management.customLiteral.value" })}
                  name="value"
                  disabled={!!disableForm}
                  value={literal?.translation?.tenantCustom ? literal?.translation?.tenantCustom?.value : ""}
                  emptyFormat={false}
                  undo={false}
                  redo={false}
                  headers={false}
                  strikeThrough={false}
                  justifyCenter={false}
                  justifyLeft={false}
                  justifyRight={false}
                  justifyFull={false}
                  indent={false}
                  outdent={false}
                  table={false}
                  link={false}
                  unlink={false}
                  image={false}
                  bold
                  italic
                  orderedList
                  unorderedList
                />
              ) : (
                <TextArea
                  validations={[required]}
                  label={toLiteral({ id: "home.literals.management.customLiteral.value" })}
                  name="value"
                  type="text"
                  disabled={!!disableForm}
                  validateOnChange={true}
                  value={literal?.translation?.tenantCustom ? literal?.translation?.tenantCustom?.value : ""}
                />
              )}
            </>
          )}

          <div className="modal-footer">
            <button className="button-primary mla" type="submit" disabled={!!disableForm}>
              {toLiteral({ id: "Save" })}
            </button>
          </div>
        </div>
      </Form>
    </>
  );
};

export default LiteralEditForm;
